import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

// Auth endpoints
export const authService = {
  getCurrentUser: () => apiClient.get('/auth/me'),
  getHealth: () => apiClient.get('/auth/health'),
};

// Team endpoints
export const teamService = {
  getAllTeams: () => apiClient.get('/teams'),
  getTeam: (id) => apiClient.get(`/teams/${id}`),
  getTeamsByCompany: (companyId) => apiClient.get(`/teams/company/${companyId}`),
  createTeam: (team) => apiClient.post('/teams', team),
  updateTeam: (id, team) => apiClient.put(`/teams/${id}`, team),
  deleteTeam: (id) => apiClient.delete(`/teams/${id}`),
  addMember: (teamId, member) => apiClient.post(`/teams/${teamId}/members`, member),
};

// Company endpoints
export const companyService = {
  getAllCompanies: () => apiClient.get('/companies'),
  getCompaniesPaginated: (page = 0, size = 50, sortField = 'name', sortDirection = 'asc', filters = {}) => {
    const { paymentMethod = 'all', billingCycle = 'all', tier = 'all' } = filters;
    return apiClient.get(`/companies/paginated?page=${page}&size=${size}&sortField=${encodeURIComponent(sortField)}&sortDirection=${encodeURIComponent(sortDirection)}&paymentMethod=${encodeURIComponent(paymentMethod)}&billingCycle=${encodeURIComponent(billingCycle)}&tier=${encodeURIComponent(tier)}`);
  },
  searchCompanies: (name) => apiClient.get(`/companies/search?name=${encodeURIComponent(name)}`),
  getCompany: (id) => apiClient.get(`/companies/${id}`),
  getWhereaboutsWeeklyStats: (id, weeks = 12) => apiClient.get(`/companies/${id}/whereabouts/weekly?weeks=${weeks}`),
  updateCompany: (id, company) => apiClient.put(`/companies/${id}`, company),
  getCompanySignupsByMonth: () => apiClient.get('/companies/signups-by-month'),
  getWhereaboutsActivity: (hours = 1, intervalMinutes = 5) => apiClient.get(`/companies/whereabouts/activity?hours=${hours}&intervalMinutes=${intervalMinutes}`),
  getWeeklyWhereaboutsByType: (startDate, endDate) => apiClient.get(`/companies/whereabouts/weekly-by-type?startDate=${startDate}&endDate=${endDate}`),
};

// User endpoints
export const userService = {
  getAllUsers: () => apiClient.get('/users'),
  getUserCount: () => apiClient.get('/users/count'),
  getUser: (id) => apiClient.get(`/users/${id}`),
  getUserNotifications: (id) => apiClient.get(`/users/${id}/notifications`),
  getUserNotificationHistory: (id) => apiClient.get(`/users/${id}/notifications/history`),
  getUsersByCompany: (companyId) => apiClient.get(`/users/company/${companyId}`),
  getDeletedUsersByCompany: (companyId) => apiClient.get(`/users/company/${companyId}/deleted`),
  getUserSignupsByMonth: () => apiClient.get('/users/signups-by-month'),
  getRecentUsers: (limit = 100) => apiClient.get(`/users/recent?limit=${limit}`),
  getSignupsActivity: (hours = 1, intervalMinutes = 5) => apiClient.get(`/users/signups-activity?hours=${hours}&intervalMinutes=${intervalMinutes}`),
  updateUser: (id, user) => apiClient.put(`/users/${id}`, user),
  joinCompany: (companyId) => apiClient.post(`/users/join-company/${companyId}`),
  searchUsers: (query) => apiClient.get(`/users/search?query=${encodeURIComponent(query)}`),
  getFirstToSignupUsers: () => apiClient.get('/users/first-to-signup'),
  bulkImportUsers: (companyId, users) => apiClient.post(`/users/bulk-import/${companyId}`, users),
};

// Department endpoints
export const departmentService = {
  getAllDepartments: () => apiClient.get('/departments'),
  getDepartmentsByCompany: (companyId) => apiClient.get(`/departments/company/${companyId}`),
};

// Office endpoints
export const officeService = {
  getAllOffices: () => apiClient.get('/offices'),
  getOffice: (id) => apiClient.get(`/offices/${id}`),
  getOfficesByCompany: (companyId) => apiClient.get(`/offices/company/${companyId}`),
  getOfficesByParent: (parentId) => apiClient.get(`/offices/parent/${parentId}`),
  updateOffice: (id, office) => apiClient.put(`/offices/${id}`, office),
  createOffice: (office) => apiClient.post('/offices', office),
};

// Desk endpoints
export const deskService = {
  getDesksByOffice: (officeId) => apiClient.get(`/desks/office/${officeId}`),
  createDesk: (officeId, desk) => apiClient.post(`/desks/office/${officeId}`, desk),
};

// Car Park Plan endpoints
export const carParkPlanService = {
  getCarParkPlansByOffice: (officeId) => apiClient.get(`/car-park-plans/office/${officeId}`),
  createCarParkPlan: (officeId, data) => apiClient.post(`/car-park-plans/office/${officeId}`, data),
  updateCarParkPlan: (id, data) => apiClient.put(`/car-park-plans/${id}`, data),
  uploadCarParkMap: (officeId, file, name) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    return apiClient.post(`/car-park-plans/office/${officeId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Office check-in IP address approval endpoints
export const ocipIpAllowlistService = {
  list: (companyId) => apiClient.get(`/companies/${companyId}/ocip-ip-allowlist`),
  listPending: () => apiClient.get('/companies/ocip-ip-allowlist/pending'),
  listAll: () => apiClient.get('/companies/ocip-ip-allowlist/all'),
  unlisted: (companyId) => apiClient.get(`/companies/${companyId}/ocip-ip-allowlist/unlisted`),
  ignored: (companyId) => apiClient.get(`/companies/${companyId}/ocip-ip-allowlist/ignored`),
  lookup: (companyId, ip) => apiClient.get(`/companies/${companyId}/ocip-ip-allowlist/lookup`, { params: { ip } }),
  add: (companyId, ipAddress, label, officeId) => apiClient.post(`/companies/${companyId}/ocip-ip-allowlist`, { ipAddress, label, officeId }),
  approve: (companyId, addressId) => apiClient.post(`/companies/${companyId}/ocip-ip-allowlist/${addressId}/approve`),
  reject: (companyId, addressId) => apiClient.post(`/companies/${companyId}/ocip-ip-allowlist/${addressId}/reject`),
  remove: (companyId, addressId) => apiClient.delete(`/companies/${companyId}/ocip-ip-allowlist/${addressId}`),
};

// Public API Key endpoints
export const publicApiKeyService = {
  getApiKeys: (companyId) => apiClient.get(`/companies/${companyId}/public-api-keys`),
  generateApiKey: (companyId) => apiClient.post(`/companies/${companyId}/public-api-keys`),
  deleteApiKey: (companyId, keyId) => apiClient.delete(`/companies/${companyId}/public-api-keys/${keyId}`),
};

// SCIM API Key endpoints
export const scimApiKeyService = {
  getApiKeys: (companyId) => apiClient.get(`/companies/${companyId}/scim-api-keys`),
  unlockApiKey: (companyId, keyId) => apiClient.post(`/companies/${companyId}/scim-api-keys/${keyId}/unlock`),
};

// Locked sign-up domain approval endpoints
export const lockedSignupDomainService = {
  list: (companyId) => apiClient.get(`/companies/${companyId}/locked-signup-domains`),
  listPending: () => apiClient.get('/companies/locked-signup-domains/pending'),
  listAll: () => apiClient.get('/companies/locked-signup-domains/all'),
  add: (companyId, domain) => apiClient.post(`/companies/${companyId}/locked-signup-domains`, { domain }),
  approve: (companyId, domainId) => apiClient.post(`/companies/${companyId}/locked-signup-domains/${domainId}/approve`),
  reject: (companyId, domainId) => apiClient.post(`/companies/${companyId}/locked-signup-domains/${domainId}/reject`),
  remove: (companyId, domainId) => apiClient.delete(`/companies/${companyId}/locked-signup-domains/${domainId}`),
};

// Staff reports
export const reportService = {
  sharedEmailDomains: ({ includePublic = false, minCompanies = 2 } = {}) =>
    apiClient.get('/reports/shared-email-domains', { params: { includePublic, minCompanies } }),
  signupConversion: ({ months = 12, grouping = 'month' } = {}) =>
    apiClient.get('/reports/signup-conversion', { params: { months, grouping } }),
  engagement: ({ weeks = 52 } = {}) =>
    apiClient.get('/reports/engagement', { params: { weeks } }),
  companyAdmins: () => apiClient.get('/reports/company-admins'),
};

// Admin home page
export const homeService = {
  getSummary: () => apiClient.get('/home/summary'),
};

// Domain-based sign-up domain approval endpoints
export const signupDomainService = {
  list: (companyId) => apiClient.get(`/companies/${companyId}/signup-domains`),
  listPending: () => apiClient.get('/companies/signup-domains/pending'),
  listAll: () => apiClient.get('/companies/signup-domains/all'),
  add: (companyId, domain) => apiClient.post(`/companies/${companyId}/signup-domains`, { domain }),
  approve: (companyId, domainId) => apiClient.post(`/companies/${companyId}/signup-domains/${domainId}/approve`),
  reject: (companyId, domainId) => apiClient.post(`/companies/${companyId}/signup-domains/${domainId}/reject`),
  remove: (companyId, domainId) => apiClient.delete(`/companies/${companyId}/signup-domains/${domainId}`),
};

// Cache endpoints
export const cacheService = {
  flushAll: () => apiClient.post('/cache/flush'),
};

// Company comment endpoints
export const companyCommentService = {
  getComments: (companyId) => apiClient.get(`/companies/${companyId}/comments`),
  createComment: (companyId, commentText) =>
    apiClient.post(`/companies/${companyId}/comments`, { commentText }),
  getRecentComments: () => apiClient.get('/company-comments/recent'),
};

// Audit Log endpoints
export const auditLogService = {
  // params: { q, action, source, userId, from, to, page, size }; from/to are inclusive yyyy-MM-dd dates
  getAuditLogsByCompany: (companyId, params = {}) =>
    apiClient.get(`/audit-logs/company/${companyId}`, { params }),
  getAuditLogFilters: (companyId) => apiClient.get(`/audit-logs/company/${companyId}/filters`),
};

// Marketing site agent endpoints
export const marketingAgentService = {
  startJob: (prompt) => apiClient.post('/marketing-agent/jobs', { prompt }),
  getJob: (id) => apiClient.get(`/marketing-agent/jobs/${id}`),
  listJobs: () => apiClient.get('/marketing-agent/jobs'),
  reply: (id, message) => apiClient.post(`/marketing-agent/jobs/${id}/reply`, { message }),
  cancelJob: (id) => apiClient.post(`/marketing-agent/jobs/${id}/cancel`),
  approveJob: (id) => apiClient.post(`/marketing-agent/jobs/${id}/approve`),
  discardJob: (id) => apiClient.post(`/marketing-agent/jobs/${id}/discard`),
  getInfo: () => apiClient.get('/marketing-agent/info'),
};

// Marketing site page editor endpoints
export const marketingSiteService = {
  getInfo: () => apiClient.get('/marketing-site/info'),
  listPages: () => apiClient.get('/marketing-site/pages'),
  getPage: (path) => apiClient.get('/marketing-site/page', { params: { path } }),
  diff: ({ path, sha, edits }) => apiClient.post('/marketing-site/page/diff', { path, sha, edits }),
  save: ({ path, sha, edits, message }) => apiClient.post('/marketing-site/page/save', { path, sha, edits, message }),
  /** Main vs production, the latest deploys, and changedFiles [{ path, status, additions, deletions, pageUrl }] plus compareUrl */
  releaseStatus: () => apiClient.get('/marketing-site/release/status'),
  deployProd: (expectedMainHead) => apiClient.post('/marketing-site/release/deploy-prod', { expectedMainHead }),
  getDeploy: (id) => apiClient.get(`/marketing-site/deploys/${encodeURIComponent(id)}`),
  siteSnapshot: () => apiClient.get('/marketing-site/site/snapshot'),
  pageLint: (path) => apiClient.get('/marketing-site/page/lint', { params: { path } }),
  siteLint: () => apiClient.get('/marketing-site/site/lint'),
  pageLinks: (path) => apiClient.get('/marketing-site/page/links', { params: { path } }),
  siteLinks: () => apiClient.get('/marketing-site/site/links'),
  /** params: { hrefs: [...] } */
  linkFixPreview: (params) => apiClient.post('/marketing-site/site/links/fix/preview', params),
  linkFixApply: ({ params, baseSha, message }) =>
    apiClient.post('/marketing-site/site/links/fix/apply', { params, baseSha, message }),
  indexStatus: () => apiClient.get('/marketing-site/site/index-status'),
  pageHistory: (path, limit = 30) => apiClient.get('/marketing-site/page/history', { params: { path, limit } }),
  /** mode: 'TEXT' (old texts in today's page) or 'FULL' (the old file exactly) */
  pageVersion: (path, ref, mode = 'TEXT') => apiClient.get('/marketing-site/page/version', { params: { path, ref, mode } }),
  /** body: { json, items: [{ jsonIndex, question?, answer? }] } with HTML values; returns { json } */
  faqSync: (body) => apiClient.post('/marketing-site/page/faq/sync', body),
  /** params: { path, sha, op: 'ADD' | 'REMOVE' | 'ADD_JSONLD' | 'ADD_TO_JSONLD', index, question, answer } */
  faqEditPreview: (params) => apiClient.post('/marketing-site/page/faq/preview', params),
  faqEditApply: ({ params, baseSha, message }) => apiClient.post('/marketing-site/page/faq/apply', { params, baseSha, message }),
  faqDrift: () => apiClient.get('/marketing-site/site/faq-drift'),
  /** params: { choices: [{ path, index, direction: 'TO_JSONLD' | 'TO_VISIBLE' }] } */
  faqDriftPreview: (params) => apiClient.post('/marketing-site/site/faq-drift/preview', params),
  faqDriftApply: ({ params, baseSha, message }) =>
    apiClient.post('/marketing-site/site/faq-drift/apply', { params, baseSha, message }),
  restore: ({ path, currentSha, ref, mode, message }) =>
    apiClient.post('/marketing-site/page/restore', { path, currentSha, ref, mode, message }),
  /** params: { sort: boolean } */
  sitemapPreview: (params) => apiClient.post('/marketing-site/site/sitemap/preview', params),
  sitemapApply: ({ params, baseSha, message }) =>
    apiClient.post('/marketing-site/site/sitemap/apply', { params, baseSha, message }),
  /** params: { content, baseSha } where baseSha is the llms.txt blob the edit started from */
  llmsPreview: (params) => apiClient.post('/marketing-site/site/llms/preview', params),
  llmsApply: ({ params, baseSha, message }) => apiClient.post('/marketing-site/site/llms/apply', { params, baseSha, message }),
  /** scope: 'header' | 'footer' */
  getChrome: (scope) => apiClient.get('/marketing-site/site/chrome', { params: { scope } }),
  /** params: { scope: 'HEADER' | 'FOOTER', edits: [{ id, value, start, end }] } */
  chromePreview: (params) => apiClient.post('/marketing-site/site/chrome/preview', params),
  chromeApply: ({ params, baseSha, message }) =>
    apiClient.post('/marketing-site/site/chrome/apply', { params, baseSha, message }),
};

/** The server's message for a failed request, for showing to the user. */
export const apiErrorMessage = (err, fallback = 'Something went wrong. Please try again.') =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

// Data Insights (social-media stat reports) endpoints
export const insightsService = {
  officeDaysByWeekday: (days = 90) => apiClient.get(`/insights/office-days-by-weekday?days=${days}`),
  whereaboutsSplit: (days = 90) => apiClient.get(`/insights/whereabouts-split?days=${days}`),
  splitDays: (days = 90) => apiClient.get(`/insights/split-days?days=${days}`),
  deskLoyalty: (minBookings = 10) => apiClient.get(`/insights/desk-loyalty?minBookings=${minBookings}`),
  meetingLeadTime: (days = 90) => apiClient.get(`/insights/meeting-lead-time?days=${days}`),
  meetingLengths: (days = 180) => apiClient.get(`/insights/meeting-lengths?days=${days}`),
  holidayPatterns: (year) => apiClient.get(`/insights/holiday-patterns${year ? `?year=${year}` : ''}`),
  workplaceHeroes: () => apiClient.get('/insights/workplace-heroes'),
  visitorWeekdays: (days = 180) => apiClient.get(`/insights/visitor-weekdays?days=${days}`),
  parkingByWeekday: (days = 90, officeId = null) => {
    const params = new URLSearchParams({ days });
    if (officeId) params.append('officeId', officeId);
    return apiClient.get(`/insights/parking-by-weekday?${params.toString()}`);
  },
  customStatusLabels: (minCompanies = 2, limit = 25) =>
    apiClient.get(`/insights/custom-status-labels?minCompanies=${minCompanies}&limit=${limit}`),
};

// Unregistered User Visit endpoints
export const unregisteredUserVisitService = {
  getVisits: (page = 0, size = 50, newDomain = null) => {
    const params = new URLSearchParams({ page, size });
    if (newDomain !== null) {
      params.append('newDomain', newDomain);
    }
    return apiClient.get(`/unregistered-user-visits?${params.toString()}`);
  },
};

// Elastic Beanstalk log viewer (CloudWatch Logs, ADMIN only)
export const logsService = {
  getSources: () => apiClient.get('/logs/sources'),
  search: ({ source, from, to, text, level, limit }) => {
    const params = new URLSearchParams({ source, from, to });
    if (text) params.append('text', text);
    if (level && level !== 'ALL') params.append('level', level);
    if (limit) params.append('limit', limit);
    return apiClient.get(`/logs/search?${params.toString()}`);
  },
  getContext: ({ source, stream, timestamp, beforeMs = 2000, afterMs = 5000 }) => {
    const params = new URLSearchParams({ source, stream, timestamp, beforeMs, afterMs });
    return apiClient.get(`/logs/context?${params.toString()}`);
  },
};

// Elastic Beanstalk environment and instance health, plus recent environment events (read only)
export const serverHealthService = {
  getOverview: (eventHours) => apiClient.get('/server-health', { params: { eventHours } }),
};

export default apiClient;
